import React, { useState } from 'react';
import Layout from '../components/layout';
import SEO from '../components/seo';
import Card from '../components/card';
import Button from '../components/atoms/Button';
import { ExternalLink, ChevronDown, ChevronUp } from 'react-feather';
import { ArrowRight, MessageCircle, Zap, SlidersHorizontal, CreditCard, HeadphonesIcon, Gift } from 'lucide-react';
import { Link } from 'gatsby';
import RoofingCalculator from '../components/sidebars/roof-calc';

const benefits = [
  { title: 'One-Stop Convenience', desc: 'We handle both your roof and financing, saving your time and doing the paperwork for you.', icon: MessageCircle },
  { title: 'Fast Approval & Quick Start', desc: 'Many programs approve in minutes, so your project can begin right away.', icon: Zap },
  { title: 'Flexible Terms', desc: 'Financing options for roofing, including deferred interest or promotional 0% APR periods.', icon: SlidersHorizontal },
  { title: 'Credit-Friendly Options', desc: 'Programs accommodate a range of credit profiles, from excellent to fair.', icon: CreditCard },
  { title: 'Expert Support', desc: 'We already have established relationships with lenders, helping troubleshoot any issues.', icon: HeadphonesIcon },
  { title: 'Exclusive Promotions', desc: 'Access special deals and limited-time offers not available directly from lenders.', icon: Gift },
];

const financingOptions = [
  {
    title: 'Contractor Financing Programs',
    desc: 'These home improvement financing programs often offer competitive rates, sometimes 0% APR promotions, with quick, easy applications and fast approvals — sometimes in minutes.',
  },
  {
    title: 'Home Equity Loans',
    desc: "Home equity loans and HELOCs let you tap your Michigan home's equity for roofing projects, often at lower interest rates than unsecured loans. HELOCs offer flexible, credit-card-style access. Approval takes longer, and your home serves as collateral.",
  },
  {
    title: 'Personal Loans',
    desc: 'Unsecured personal loans offer fast, simple roofing financing with no collateral, typically repaid over 2–7 years. Rates vary by credit, often higher than home equity loans, but borrowers with good credit can secure reasonable terms.',
  },
  {
    title: 'Credit Cards',
    desc: 'Credit cards with 0% introductory APR can work for small roofing repairs, but full roof replacements are usually not ideal due to high interest rates and credit limits.',
  },
];

const comparisonData = [
  { option: 'Contractor Financing', term: '12–24 months', interest: '0–10%', speed: 'Minutes' },
  { option: 'Home Equity Loan / HELOC', term: '10–20 years', interest: '4–8%', speed: 'Weeks' },
  { option: 'Personal Loans', term: '3–7 years', interest: '6–15%', speed: 'Days' },
  { option: 'Credit Card', term: 'Varies', interest: '18–25%', speed: 'Immediate' },
];

const faqs = [
  { q: 'Can my insurance pay for a new roof?', a: 'Homeowners insurance may cover roof replacement if the damage is caused by a covered peril like a storm or hail. Contact your insurance provider to review your policy and file a claim if applicable.' },
  { q: 'Should you put a roof on a credit card?', a: 'For small repairs, a 0% intro APR credit card can make sense. For full replacements, contractor financing or a home equity loan is usually a better option due to lower rates and higher limits.' },
  { q: 'What time of year is the cheapest to replace a roof?', a: 'Late fall and winter tend to have lower demand, which can mean lower prices and faster scheduling. However, weather can be a factor in Michigan.' },
  { q: 'Can my insurance pay for a new roof?', a: 'If your roof was damaged by a covered event such as a storm, hail, or fallen tree, your homeowners insurance may help cover part or all of the replacement cost. Review your policy details and file a claim promptly.' },
  { q: 'How long can you finance a new roof?', a: 'Depending on the financing type, terms range from 12 months (contractor financing) to up to 20 years (home equity loans). Michigan Saves offers up to 15 years.' },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-neutral-200 py-4">
      <button
        className="flex w-full justify-between items-center text-left font-medium text-gray-900 hover:text-brand-600 transition"
        onClick={() => setOpen(!open)}
      >
        <span>{q}</span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && <p className="mt-3 text-gray-600 text-sm leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Financing({ props }) {
  return (
    <Layout pageProps={props}>
      <SEO
        title={'Roofing Financing Options in Michigan | Roach Home Improvement'}
        description={
          'Roach Home Improvement offers flexible roofing financing options in Michigan including Michigan Saves and Synchrony. Get approved and start your project fast.'
        }
      />

      <section className="bg-neutral-50 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8 md:py-12 flex flex-wrap">
        <div className="w-full md:w-2/3 md:pr-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Roofing Financing Options in Michigan</h1>
          <p className="mt-3 text-lg text-gray-700">
            Roach Home Improvement, as a licensed roofing contractor in Michigan, provides roofing financing options for
            repair, replacement. Get in touch with us and our specialists will walk you through our simplified process.
          </p>

          {/* Michigan Saves */}
          <div className="mt-8">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800">Michigan Saves Financing</h2>
            <div className="bg-white shadow-md rounded-xl p-6 mt-4">
              <img
                src={'/assets/cropped-michigan-saves-logo-1.png'}
                alt="Michigan Saves Financing"
                className="mb-4 block h-10"
              />
              <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                <li>Quick and Easy Approvals: Get approved swiftly for loans up to $100,000 for up to 15 years.</li>
                <li>No Hidden Fees: Enjoy zero origination fees or closing costs.</li>
                <li>Flexible Repayment: No prepayment penalties. Pay off your loan at any time without extra charges.</li>
                <li>Secure and Simple: Sign your loan documents electronically.</li>
                <li>Tailored Terms: Choose a loan term that fits your budget perfectly.</li>
              </ul>
              <div className="mt-4">
                <Button color="outline" href={'https://michigansaves.org/'} target="_blank" className="inline-flex items-center">
                  Learn more <ExternalLink size={14} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Synchrony */}
          <div className="mt-8">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800">Synchrony Financing</h2>
            <div className="bg-white shadow-md rounded-xl p-6 mt-4">
              <p className="font-semibold text-yellow-600 text-lg mb-1">synchrony</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                <li>Long-Term Financing: Secure financing for up to 20 years.</li>
                <li>Instant Approvals: Receive initial full approvals in just seconds.</li>
              </ul>
              <div className="mt-4">
                <Button color="outline" href={'https://www.synchrony.com/'} target="_blank" className="inline-flex items-center">
                  Learn more <ExternalLink size={14} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Benefits section */}
          <div className="mt-12">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-6">Benefits of Financing from Roof Contractors</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {benefits.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.title} className="bg-white rounded-xl border border-neutral-200 p-5 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 bg-red-50 rounded-full flex items-center justify-center">
                      <Icon size={24} className="text-brand-600" />
                    </div>
                    <p className="font-semibold text-gray-900 text-sm mb-1">{b.title}</p>
                    <p className="text-gray-600 text-sm">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Available options */}
          <div className="mt-12">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-2">
              Available Roofing Financing <span className="text-brand-600">Options in Michigan</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {financingOptions.map((o) => (
                <div key={o.title} className="bg-white rounded-xl border border-neutral-200 p-5">
                  <p className="font-semibold text-gray-900 text-sm mb-2">{o.title}</p>
                  <p className="text-gray-600 text-sm">{o.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison table */}
          <div className="mt-12 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-neutral-300">
                  <th className="text-left py-3 pr-4 font-semibold text-gray-700">Financing Option</th>
                  <th className="text-left py-3 pr-4 font-semibold text-gray-700">Typical Term</th>
                  <th className="text-left py-3 pr-4 font-semibold text-gray-700">Interest</th>
                  <th className="text-left py-3 font-semibold text-gray-700">Approval Speed</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row) => (
                  <tr key={row.option} className="border-b border-neutral-100">
                    <td className="py-3 pr-4 text-gray-800 font-medium">{row.option}</td>
                    <td className="py-3 pr-4 text-gray-600">{row.term}</td>
                    <td className="py-3 pr-4 text-gray-600">{row.interest}</td>
                    <td className="py-3 text-gray-600">{row.speed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-full md:w-1/3 md:pl-4 space-y-4 md:space-y-6 mt-8 md:mt-0">
          <Card sidebar={{ content: { content: '{{ContactInfo}}' } }} />
          <Card sidebar={{ content: { content: '{{WhyUs}}' } }} />
        </div>
      </section>

      {/* Roofing Calculator CTA Banner */}
      <section className="relative overflow-hidden py-16 md:py-20 flex items-center justify-center">
        <RoofingCalculator />
      </section>

      <section className="bg-neutral-50 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8 md:py-12">
        {/* FAQs */}
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">FAQs on Roofing Financing in MI</h2>
          {faqs.map((faq, i) => (
            <FAQItem key={i} q={faq.q} a={faq.a} />
          ))}
        </div>

        {/* Disclaimer */}
        <div className="mt-8 bg-neutral-100 border border-neutral-200 rounded-xl p-5 text-sm text-gray-500">
          <p className="font-semibold text-gray-700 mb-1">Disclaimer</p>
          <p>
            This content is for informational purposes only and is not financial advice. Financing options and
            eligibility may differ. Please consult a financial advisor, lender, or insurance provider to find the best
            solution for your situation.
          </p>
        </div>
      </section>
    </Layout>
  );
}
